import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPrint,
  faFilePdf,
  faBuilding,
  faUser,
  faCalendarAlt,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faCamera,
  faSpinner,
  faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';
import html2canvas from 'html2canvas';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Logo from '../../../assetes/logos.png';

// ============================================================
// CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('InvoiceCacheDB');
db.version(1).stores({ invoices: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedInvoice(key) {
  const entry = await db.invoices.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_DURATION) return entry.data;
  return null;
}
async function setCachedInvoice(key, data) {
  await db.invoices.put({ key, data, timestamp: Date.now() });
}
async function getStaleInvoice(key) {
  const entry = await db.invoices.get(key);
  return entry ? entry.data : null;
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES
// ============================================================
const pendingFetches = new Map();

// ============================================================
// UTILITAIRE FETCH JSON AVEC SIGNAL
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

const API_URL = `${API_CONFIG2.apiUrl}factures/api_invoice_data.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const InvoiceView = ({ factureId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState('cache');
  const invoiceRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const abortControllerRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // FONCTION DE FETCH AVEC DÉDOUBLONNAGE ET CACHE
  // ============================================================
  const fetchInvoice = useCallback(async (forceRefresh = false, retryCount = 0) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `invoice_${factureId}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setData(result);
          setDataSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
          setError(null);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives pour éviter les boucles infinies
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        setError('Erreur : trop de tentatives. Veuillez réessayer plus tard.');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    const fetchPromise = (async () => {
      try {
        // 1. Cache frais (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedInvoice(cacheKey);
          if (cached && isMounted.current) {
            setData(cached);
            setDataSource('cache');
            setLoading(false);
            setRefreshing(false);
            setError(null);
            return cached;
          }
        }

        // 2. Requête réseau
        const url = `${API_URL}?id=${factureId}`;
        const response = await fetch(url, { signal: controller.signal });

        // Gestion 429
        if (response.status === 429) {
          const stale = await getStaleInvoice(cacheKey);
          if (stale && isMounted.current) {
            setData(stale);
            setDataSource('cache (périmé)');
            setLoading(false);
            setRefreshing(false);
            setError('Utilisation du cache périmé (429)');
            return stale;
          }
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchInvoice(forceRefresh, retryCount + 1);
        }

        if (!response.ok) {
          const stale = await getStaleInvoice(cacheKey);
          if (stale && isMounted.current) {
            setData(stale);
            setDataSource('cache (périmé)');
            setLoading(false);
            setRefreshing(false);
            setError('Utilisation du cache périmé (erreur réseau)');
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        const json = await response.json();
        if (json.status === 'success') {
          if (isMounted.current) {
            setData(json);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            setLoading(false);
            setRefreshing(false);
            setError(null);
            await setCachedInvoice(cacheKey, json);
          }
          return json;
        } else {
          throw new Error(json.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') return null;
        // Dernier recours : cache périmé
        const stale = await getStaleInvoice(cacheKey);
        if (stale && isMounted.current) {
          setData(stale);
          setDataSource('cache (périmé)');
          setLoading(false);
          setRefreshing(false);
          setError(`Utilisation du cache périmé : ${err.message}`);
          return stale;
        }
        if (isMounted.current) {
          setError(err.message || 'Erreur de chargement');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [factureId]);

  // ============================================================
  // CHARGEMENT INITIAL ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchInvoice(false);
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      pendingFetches.clear(); // Nettoyer pour éviter les fuites
    };
  }, [fetchInvoice]);

  // ============================================================
  // GESTIONNAIRES (Print, Capture, PDF)
  // ============================================================
  const handlePrint = () => {
    window.print();
  };

  const handleCapture = async () => {
    if (!invoiceRef.current) return;
    setCapturing(true);
    try {
      const canvas = await html2canvas(invoiceRef.current, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `facture_${data?.facture?.id || 'export'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error('Erreur lors de la capture :', err);
    } finally {
      setCapturing(false);
    }
  };

  const handlePDF = () => {
    window.open(
      `${API_CONFIG2.apiUrl}factures/api_generate_invoice.php?id=${data?.facture?.id}`,
      '_blank'
    );
  };

  // ============================================================
  // RENDU DES ÉTATS DE CHARGEMENT / ERREUR
  // ============================================================
  if (loading && !data) {
    return (
      <div className="d-flex justify-content-center py-5" style={{ background: '#000', minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status" />
        <span className="ms-3 text-white">Chargement de la facture...</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="text-center py-5 text-danger" style={{ background: '#000', minHeight: '100vh' }}>
        <FontAwesomeIcon icon={faExclamationTriangle} className="mb-3" size="3x" />
        <p>{error}</p>
        <button className="btn-action primary" onClick={() => fetchInvoice(true)}>
          Réessayer
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { facture, entreprise, calculs } = data;
  const { montantHT, tva, montantTTC } = calculs;

  // Valeurs par défaut pour l'entreprise
  const entrepriseNom = entreprise?.nom || 'Mon Entreprise';
  const entrepriseAdresse = entreprise?.adresse || 'Adresse non renseignée';
  const entrepriseTel = entreprise?.telephone || 'N/C';
  const entrepriseEmail = entreprise?.email || 'N/C';
  const entrepriseRccm = entreprise?.rccm || 'N/C';
  const entrepriseIdnat = entreprise?.idnat || 'N/C';
  const entrepriseNimpot = entreprise?.nimpot || 'N/C';
  const entrepriseCompte = entreprise?.compteBancaire || null;

  // ============================================================
  // RENDU PRINCIPAL AVEC BADGE SOURCE
  // ============================================================
  return (
    <>
      <style>{`
        /* ============ OVERALL PAGE ============ */
        body {
          background: #000 !important;
          margin: 0;
          padding: 20px;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        /* ============ INVOICE CONTAINER ============ */
        .invoice-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: clamp(1.2rem, 3vw, 2.5rem);
          background: #ffffff;
          color: #1e293b;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
          transition: all 0.2s;
        }

        /* ============ BUTTONS ============ */
        .btn-action {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid #e2e8f0;
          background: #f8fafc;
          color: #1e293b;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          transition: all 0.15s ease;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          min-height: 40px;
          touch-action: manipulation;
        }
        .btn-action:hover:not(:disabled) {
          background: #f1f5f9;
          border-color: #94a3b8;
          transform: translateY(-1px);
        }
        .btn-action:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-action.primary {
          background: #2563eb;
          border: none;
          color: #fff;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
        }
        .btn-action.primary:hover:not(:disabled) {
          background: #1d4ed8;
          box-shadow: 0 6px 16px rgba(37, 99, 235, 0.4);
        }
        .btn-action.success {
          background: #16a34a;
          border: none;
          color: #fff;
          box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
        }
        .btn-action.success:hover:not(:disabled) {
          background: #15803d;
        }
        .badge-source {
          font-size: clamp(0.55rem, 0.8vw, 0.7rem);
          padding: 4px 12px;
          border-radius: 40px;
          background: #eef2ff;
          color: #4338ca;
          border: 1px solid #c7d2fe;
          white-space: nowrap;
          font-weight: 500;
        }
        .badge-source.live {
          background: #d1fae5;
          color: #065f46;
          border-color: #6ee7b7;
        }
        .badge-source.warning {
          background: #fef3c7;
          color: #92400e;
          border-color: #fcd34d;
        }

        /* ============ HEADER ============ */
        .invoice-header {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 1.8rem;
          margin-bottom: 2rem;
        }
        .company-logo img {
          max-height: 70px;
          max-width: 200px;
        }
        .company-info {
          text-align: right;
        }
        .company-info h1 {
          margin: 0 0 0.4rem 0;
          font-size: clamp(1.4rem, 3vw, 2rem);
          font-weight: 700;
          color: #0f172a;
          letter-spacing: -0.5px;
        }
        .company-info p {
          margin: 3px 0;
          font-size: clamp(0.75rem, 1vw, 0.9rem);
          color: #475569;
        }

        /* ============ INVOICE TITLE ============ */
        .invoice-title {
          font-size: clamp(1.6rem, 4vw, 2.4rem);
          font-weight: 800;
          text-align: center;
          margin: 1.5rem 0 2rem 0;
          color: #0f172a;
          letter-spacing: -0.5px;
        }
        .invoice-title span {
          color: #2563eb;
        }

        /* ============ CLIENT BLOCK ============ */
        .client-block {
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          background: #f8fafc;
          border-radius: 16px;
          padding: clamp(1rem, 2vw, 1.5rem);
          margin-bottom: 2rem;
          border: 1px solid #e2e8f0;
        }
        .client-block .box {
          flex: 1;
          min-width: 180px;
        }
        .client-block .box p {
          margin: 6px 0;
          font-size: clamp(0.85rem, 1.1vw, 0.95rem);
          color: #334155;
        }
        .client-block .box strong {
          font-size: clamp(0.95rem, 1.2vw, 1.05rem);
          color: #0f172a;
        }
        .client-block .box .icon-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #475569;
        }

        /* ============ TABLE ============ */
        .invoice-table {
          width: 100%;
          border-collapse: collapse;
          margin: 2rem 0;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }
        .invoice-table th {
          background: #f1f5f9;
          color: #0f172a;
          font-weight: 600;
          text-transform: uppercase;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          letter-spacing: 0.5px;
          padding: 12px 14px;
          text-align: left;
          border-bottom: 2px solid #e2e8f0;
        }
        .invoice-table td {
          padding: 12px 14px;
          border-bottom: 1px solid #f1f5f9;
          font-size: clamp(0.85rem, 1vw, 0.95rem);
          color: #1e293b;
        }
        .invoice-table tr:last-child td {
          border-bottom: none;
        }

        /* ============ TOTALS ============ */
        .invoice-totals {
          display: flex;
          justify-content: flex-end;
          margin-top: 2rem;
        }
        .totals-wrapper {
          width: 100%;
          max-width: 320px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 1.2rem 1.8rem;
        }
        .totals-wrapper .row-line {
          display: flex;
          justify-content: space-between;
          padding: 6px 0;
          font-size: clamp(0.85rem, 1vw, 0.95rem);
          color: #475569;
        }
        .totals-wrapper .grand-total {
          display: flex;
          justify-content: space-between;
          font-weight: 700;
          font-size: clamp(1rem, 1.4vw, 1.2rem);
          padding: 12px 0 0 0;
          border-top: 2px solid #e2e8f0;
          margin-top: 6px;
          color: #0f172a;
        }

        /* ============ FOOTER ============ */
        .invoice-footer {
          margin-top: 3rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: clamp(0.75rem, 0.9vw, 0.85rem);
          color: #64748b;
          flex-wrap: wrap;
          gap: 10px;
        }
        .invoice-footer .bank-info {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        /* ============ PRINT ============ */
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; padding: 0 !important; }
          .invoice-container {
            box-shadow: none !important;
            border-radius: 0 !important;
            padding: 1.5rem !important;
            background: white !important;
            color: black !important;
          }
          .client-block { background: #f9f9f9 !important; }
          .invoice-table th { background: #eee !important; }
          .totals-wrapper { background: #f9f9f9 !important; }
        }

        /* ============ UTILITY ============ */
        .text-end { text-align: right; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .gap-2 { gap: 0.5rem; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .mt-2 { margin-top: 0.5rem; }
        .fw-bold { font-weight: 600; }
        .ms-3 { margin-left: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }

        /* ============ RESPONSIVE ============ */
        @media (max-width: 768px) {
          .invoice-header {
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .company-info {
            text-align: center;
            margin-top: 1rem;
          }
          .client-block {
            flex-direction: column;
            align-items: stretch;
          }
          .client-block .box {
            margin-bottom: 0.5rem;
          }
          .client-block .box.text-end {
            text-align: left !important;
          }
          .invoice-totals {
            justify-content: center;
          }
          .totals-wrapper {
            max-width: 100%;
          }
          .btn-action {
            padding: 6px 12px;
            font-size: 0.7rem;
            min-height: 34px;
          }
          .invoice-container {
            padding: 1rem;
          }
          .invoice-table th,
          .invoice-table td {
            padding: 8px 10px;
          }
        }
        @media (max-width: 480px) {
          .btn-action {
            font-size: 0.65rem;
            padding: 4px 10px;
            min-height: 30px;
          }
          .company-logo img {
            max-height: 50px;
          }
        }
      `}</style>

      <div className="invoice-container">
        {/* Boutons d'action – hors impression */}
        <div className="no-print mb-4 d-flex flex-wrap gap-2 align-items-center">
          <button className="btn-action primary" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} /> Imprimer
          </button>
          <button className="btn-action" onClick={handlePDF}>
            <FontAwesomeIcon icon={faFilePdf} /> Télécharger PDF
          </button>
          <button className="btn-action success" onClick={handleCapture} disabled={capturing}>
            {capturing ? (
              <><FontAwesomeIcon icon={faSpinner} spin className="me-1" /> Capture...</>
            ) : (
              <><FontAwesomeIcon icon={faCamera} /> Capturer (PNG)</>
            )}
          </button>
          <button className="btn-action" onClick={() => fetchInvoice(true)} disabled={refreshing}>
            <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} /> Rafraîchir
          </button>
          {/* Badge source */}
          <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
            {dataSource === 'cache' ? '📦 Cache' :
             dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
             dataSource === 'cache (partagé)' ? '🔄 Partage' :
             dataSource === 'live' ? '🔄 Live' :
             dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
          </span>
        </div>

        {/* La facture */}
        <div ref={invoiceRef}>
          {/* En-tête */}
          <div className="invoice-header">
            <div className="company-logo">
              <img src={Logo} alt="Logo" />
            </div>
            <div className="company-info">
              <h1>{entrepriseNom}</h1>
              <p>{entrepriseAdresse}</p>
              <p>Tél : {entrepriseTel} | Email : {entrepriseEmail}</p>
              <p>RCCM : {entrepriseRccm} | Id.Nat : {entrepriseIdnat} | N° Impôt : {entrepriseNimpot}</p>
            </div>
          </div>

          {/* Titre */}
          <div className="invoice-title">
            FACTURE <span>N° {facture.id}</span>
          </div>

          {/* Client / dates */}
          <div className="client-block">
            <div className="box">
              <strong>
                <FontAwesomeIcon icon={faUser} className="me-2" />
                Facturé à
              </strong>
              <p className="mt-2">{facture.client_nom || 'Client inconnu'}</p>
              <p>{facture.adresse || 'Adresse non renseignée'}</p>
              <p>Email : {facture.email || 'N/C'}</p>
              <p>Tél : {facture.telephone || 'N/C'}</p>
            </div>
            <div className="box text-end">
              <div className="icon-label justify-content-end mb-2">
                <FontAwesomeIcon icon={faCalendarAlt} />
                <strong>Date :</strong> {facture.date}
              </div>
              <div className="icon-label justify-content-end mb-2">
                <FontAwesomeIcon icon={faClock} />
                <strong>Échéance :</strong> {facture.echeance}
              </div>
              <div className="mt-2">
                <strong>Statut :</strong> {renderStatusBadge(facture.statut)}
              </div>
            </div>
          </div>

          {/* Tableau des articles */}
          <table className="invoice-table">
            <thead>
              <tr>
                <th>Désignation</th>
                <th>Montant HT</th>
                <th>TVA (16%)</th>
                <th>Total TTC</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{facture.details || 'Aucun détail'}</td>
                <td>{montantHT.toFixed(2)} $</td>
                <td>{tva.toFixed(2)} $</td>
                <td className="fw-bold">{montantTTC.toFixed(2)} $</td>
              </tr>
            </tbody>
          </table>

          {/* Totaux */}
          <div className="invoice-totals">
            <div className="totals-wrapper">
              <div className="row-line">
                <span>Total HT</span>
                <span>{montantHT.toFixed(2)} $</span>
              </div>
              <div className="row-line">
                <span>TVA (16%)</span>
                <span>{tva.toFixed(2)} $</span>
              </div>
              <div className="grand-total">
                <span>Total TTC</span>
                <span>{montantTTC.toFixed(2)} $</span>
              </div>
            </div>
          </div>

          {/* Pied de page */}
          <div className="invoice-footer">
            <div>Merci de votre confiance.</div>
            {entrepriseCompte && (
              <div className="bank-info">
                <FontAwesomeIcon icon={faBuilding} />
                <span>Compte bancaire : {entrepriseCompte}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================================
// FONCTION UTILITAIRE RENDER STATUS BADGE (hors composant)
// ============================================================
const renderStatusBadge = (statut) => {
  let icon = faCheckCircle;
  let color = '#2563eb';
  let bg = '#eff6ff';
  if (statut === 'En attente') {
    icon = faClock;
    color = '#d97706';
    bg = '#fffbeb';
  } else if (statut === 'Impayée') {
    icon = faExclamationTriangle;
    color = '#dc2626';
    bg = '#fef2f2';
  }
  return (
    <span
      style={{
        background: bg,
        color: color,
        padding: '4px 14px',
        borderRadius: '20px',
        fontSize: '0.8rem',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: '1px solid rgba(0,0,0,0.05)',
      }}
    >
      <FontAwesomeIcon icon={icon} />
      {statut}
    </span>
  );
};

export default InvoiceView;